/**
 * Formata o CPF ou CNPJ usando os seguintes critérios:
 * - `'000.000.000-00'` para CPF (11 Digitos);
 * - `'00.000.000/0000-00'` para CNPJ (14 Digitos);
 * - Caso inválido retorna o próprio parâmetro `cpfCnpj`.
 *
 * @param cpfCnpj Documento (CPF ou CNPJ) a ser formatado
 * @returns Valor com a máscara aplicada ou
 *   o próprio parâmetro `cpfCnpj` caso seja inválido
 */
export function formatarCNPJOrCpf(cpfCnpj: string, isOnlyCnpj: boolean = false) {
  const cnpjAux = cpfCnpj.replace(/[.\-/\s]/g, '');

  if (cnpjAux.length === 11 && !isOnlyCnpj) {
    const cpfAux = cnpjAux.replace(/\D/g, '');
    if (cpfAux.length === 11) return cpfAux.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    else return cpfCnpj;
  } else if (cnpjAux.length === 14) {
    return cnpjAux.replace(
      /^([A-Za-z0-9]{2})([A-Za-z0-9]{3})([A-Za-z0-9]{3})([A-Za-z0-9]{4})([A-Za-z0-9]{2})$/,
      '$1.$2.$3/$4-$5'
    );
  } else {
    return cpfCnpj;
  }
}

/**
 * Retorna se o documento é um CPF ou um CNPJ usando os seguintes critérios:
 * - `'CPF'` - 11 Digitos;
 * - `'CNPJ'` - 14 Digitos;
 * - `''` - Nenhum dos dois.
 *
 * @param cpfCnpj Documento a ser validado
 * @returns O tipo do documento, `''` se inválido
 */
export function isCPForCNPJ(cpfCnpj: string) {
  const cnpjAux = cpfCnpj.replace(/[.\-/\s]/g, '');

  if (cnpjAux.length === 11) {
    const cpfAux = cnpjAux.replace(/\D/g, '');
    if (cpfAux.length === 11) return 'CPF';
    else return '';
  } else if (cnpjAux.length === 14) {
    return 'CNPJ';
  } else {
    return '';
  }
}

/**
 * Combina as funções `formatarCNPJOrCpf` e `isCPForCNPJ`
 * em uma única *string*:
 * - `'CPF 000.000.000-00'` para CPF (11 Digitos);
 * - `'CNPJ 00.000.000/0000-00'` para CNPJ (14 Digitos);
 * - Caso inválido retorna o próprio parâmetro `cpfCnpj`.
 *
 * @param cpfCnpj Documento (CPF ou CNPJ) a ser formatado
 * @returns Valor com a máscara aplicada ou
 *   o próprio parâmetro `cpfCnpj` caso seja inválido
 * @see formatarCNPJOrCpf
 * @see isCPForCNPJ
 */
export function maskSelectCpfCnpj(cpfCnpj: string) {
  return `${isCPForCNPJ(cpfCnpj)} ${formatarCNPJOrCpf(cpfCnpj)}`.trim();
}

/**
 * Retorna apenas os digitos de um CPF/CNPJ, sem nenhuma máscara.
 *
 * @param cpfCnpj Documento (CPF ou CNPJ)
 * @returns Digitos em *string* do documento, sem mascára aplicada
 */
export function unmaskSelectCpfCnpj(cpfCnpj: string) {
  return cpfCnpj.replace(/^(CNPJ|CPF)\s*/i, '').replace(/[.\-/\s]/g, '');
}
